import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, KeyRound } from 'lucide-react';
import { formatApiError } from '../lib/utils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Self-service PIN change dialog. Any authenticated user can update their own
 * 4-digit PIN. Admins with a password on file must confirm it; PIN-only users
 * (e.g. barbers) can skip that field.
 */
export default function ChangePinDialog({ open, onOpenChange, requirePassword = true }) {
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => { setPin(''); setConfirmPin(''); setCurrentPassword(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(pin)) { toast.error('El PIN debe tener exactamente 4 dígitos'); return; }
    if (pin !== confirmPin) { toast.error('Los PIN no coinciden'); return; }
    if (requirePassword && !currentPassword) { toast.error('Ingresa tu contraseña actual'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/auth/change-pin`, {
        new_pin: pin,
        current_password: currentPassword || undefined,
      });
      toast.success('PIN actualizado');
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err, 'No se pudo actualizar el PIN'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-md" data-testid="change-pin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-slate-500" />
            Cambiar mi PIN
          </DialogTitle>
          <DialogDescription>
            Usa 4 dígitos. Este PIN te sirve para el checador y el login rápido.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pin-new">Nuevo PIN</Label>
            <Input
              id="pin-new"
              inputMode="numeric"
              autoComplete="new-password"
              maxLength={4}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="0000"
              data-testid="input-new-pin"
              className="text-lg tracking-widest font-mono text-center"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pin-confirm">Confirmar PIN</Label>
            <Input
              id="pin-confirm"
              inputMode="numeric"
              autoComplete="new-password"
              maxLength={4}
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="0000"
              data-testid="input-confirm-pin"
              className="text-lg tracking-widest font-mono text-center"
            />
          </div>
          {requirePassword && (
            <div className="space-y-2">
              <Label htmlFor="pin-current-pwd">Contraseña actual</Label>
              <Input
                id="pin-current-pwd"
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                data-testid="input-current-password"
              />
              <p className="text-xs text-slate-500">
                Requerida para confirmar que eres tú.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => { reset(); onOpenChange(false); }}
              disabled={saving}
              data-testid="btn-cancel-change-pin"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="btn-save-change-pin"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
